<template>
  <div v-if="event" class="event_detail">
    <BContainer class="mt-3">
      <h1 class="event_title lh-sm mb-2">{{ event.title }}</h1>
      <div class="mb-3 text-secondary small">
        <span v-if="event.human_date" class="me-3">
          <Icon name="fa6-regular:calendar" class="icon-inline" />
          {{ event.human_date }}
        </span>
        <span v-if="event.human_time">
          <Icon name="fa6-regular:clock" class="me-1 icon-inline" />
          {{ event.human_time }}
        </span>
      </div>

      <BRow>
        <BCol md="8">
          <p class="mb-0">
            <span v-if="event.place"><b>{{ t('Where') }}:</b> {{ event.place }} <br /></span>
            <span v-if="event.meeting_place"><b>{{ t('Meeting place') }}:</b> {{ event.meeting_place }} <br /></span>
            <span v-if="event.when"><b>{{ t('When') }}:</b> {{ event.when }} <br /></span>
            <span v-if="event.working_time"><b>{{ t('Working time') }}:</b> {{ event.working_time }} <br /></span>
          </p>
        </BCol>
        <BCol class="mb-3">
          <EventActions :event="event" @reg_action="$emit('reg_action', $event)" />
        </BCol>
      </BRow>

      <div v-if="event.description" class="event-description mt-4" v-html="event.description" />

      <div v-if="event.coords" class="event-map-wrap mt-4">
        <yandex-map
          height="300px"
          width="100%"
          :settings="mapSettings"
        >
          <yandex-map-default-scheme-layer />
          <yandex-map-default-features-layer />
          <yandex-map-controls :settings="{ position: 'right' }">
            <yandex-map-zoom-control />
          </yandex-map-controls>
          <yandex-map-default-marker :settings="markerSettings">
            <template #popup>
              <div class="event-map-popup">
                <EventCard :event="event" />
              </div>
            </template>
          </yandex-map-default-marker>
        </yandex-map>
      </div>

      <div v-if="coverImageUrl" class="ratio ratio-16x9 mt-4">
        <img
          :src="coverImageUrl"
          class="w-100 h-100 object-fit-cover"
          loading="lazy"
          :alt="event.title ?? ''"
        />
      </div>
    </BContainer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  YandexMap,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapDefaultSchemeLayer,
  YandexMapZoomControl,
} from 'vue-yandex-maps'
import EventActions, { type EventReg } from './EventActions.vue'
import EventCard from './EventCard.vue'

interface EventCoords {
  latitude: number
  longitude: number
}

export interface EventDetailModel extends EventReg {
  images?: string[] | null
  human_date?: string
  human_time?: string
  place?: string
  meeting_place?: string
  when?: string
  working_time?: string
  description?: string
  coords?: EventCoords | null
}

const props = defineProps<{
  event: EventDetailModel
}>()

defineEmits<{
  reg_action: [payload: unknown]
}>()

const { t } = useI18n({ useScope: 'local' })

const coverImageUrl = computed(() => {
  const imgs = props.event.images
  if (imgs?.length && imgs[0]) return String(imgs[0])
  return ''
})

/** Yandex Maps 3.0 uses [longitude, latitude] */
const mapCenterLngLat = computed((): [number, number] => {
  const c = props.event.coords
  if (!c) return [0, 0]
  return [c.longitude, c.latitude]
})

const mapSettings = computed(() => ({
  location: {
    center: mapCenterLngLat.value,
    zoom: 16,
  },
  behaviors: ['drag', 'scrollZoom', 'dblClick', 'pinchZoom'],
}))

const markerSettings = computed(() => {
  const c = props.event.coords
  const coords: [number, number] = c ? [c.longitude, c.latitude] : [0, 0]
  return {
    coordinates: coords,
    title: props.event.title ?? '',
    popup: {},
  }
})
</script>

<i18n lang="json">
{
  "en": {
    "Where": "Where",
    "Meeting place": "Meeting place",
    "When": "When",
    "Working time": "Working time"
  },
  "ru": {
    "Where": "Адрес",
    "Meeting place": "Место встречи",
    "When": "Когда",
    "Working time": "Время работы"
  }
}
</i18n>

<style lang="scss" scoped>
.event-map-wrap {
  min-height: 300px;
}

.event-map-popup {
  max-width: min(420px, 92vw);
  max-height: 70vh;
  overflow: auto;
}

.event_detail {
  .event_title {
    font-size: 2.2rem;
  }
}

.icon-inline {
  vertical-align: -0.125em;
}

.event-description :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
